import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ClipboardList, Plus, Trash2, ExternalLink, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CareerPipeline } from "@/components/CareerPipeline";

const STATUS_META = {
  saved: { label: "Saved", cls: "bg-secondary text-muted-foreground" },
  preparing: { label: "Preparing", cls: "bg-sky-500/10 text-sky-400" },
  applied: { label: "Applied", cls: "bg-indigo-500/10 text-indigo-400" },
  interview: { label: "Interview", cls: "bg-amber-500/10 text-amber-400" },
  offer: { label: "Offer 🎉", cls: "bg-emerald-500/10 text-emerald-400" },
  rejected: { label: "Rejected", cls: "bg-red-500/10 text-red-400" },
  withdrawn: { label: "Withdrawn", cls: "bg-secondary text-muted-foreground" },
  closed: { label: "Closed", cls: "bg-secondary text-muted-foreground" },
};
const ORDER = Object.keys(STATUS_META);

export default function ApplicationsPage() {
  const [data, setData] = useState(null);
  const [params] = useSearchParams();
  const [showForm, setShowForm] = useState(!!(params.get("role") || params.get("platform")));

  const load = useCallback(() => {
    api.get("/applications").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;
  const active = data.applications.filter((a) => !["rejected", "withdrawn", "closed"].includes(a.status));

  return (
    <div className="mx-auto max-w-4xl px-6 py-10 md:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Execution loop</p>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-medium tracking-tight sm:text-4xl">
          <ClipboardList className="mr-1 inline h-6 w-6" /> Job Tracker
        </h1>
        <Button onClick={() => setShowForm((v) => !v)} data-testid="application-add-btn">
          {showForm ? <X className="mr-1 h-4 w-4" /> : <Plus className="mr-1 h-4 w-4" />}
          {showForm ? "Close" : "Track application"}
        </Button>
      </div>
      <CareerPipeline refreshKey={data.applications.length} />
      <p className="mt-2 text-muted-foreground">Every CV you send, tracked from Applied to Offer — so no opportunity slips through.</p>

      <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="application-stats">
        {["applied", "interview", "offer", "rejected"].map((s) => (
          <div key={s} className="rounded-xl border border-border bg-card px-4 py-3">
            <p className="text-2xl font-semibold">{data.counts[s] || 0}</p>
            <p className="text-xs text-muted-foreground">{STATUS_META[s].label.replace(" 🎉", "")}</p>
          </div>
        ))}
      </div>

      {showForm && <AddForm prefill={{ role: params.get("role") || "", platform: params.get("platform") || "" }} onDone={() => { setShowForm(false); load(); }} />}

      {data.applications.length === 0 ? (
        <p className="mt-8 rounded-2xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="applications-empty">
          No applications tracked yet. Build a CV, apply on a job board, then log it here — Zuntria keeps your pipeline honest.
        </p>
      ) : (
        <ul className="mt-6 space-y-3">
          {[...active, ...data.applications.filter((a) => !active.includes(a))].map((a) => (
            <AppRow key={a.application_id} app={a} onChange={load} />
          ))}
        </ul>
      )}
    </div>
  );
}

function AddForm({ onDone, prefill = {} }) {
  const [form, setForm] = useState({ company: "", role: prefill.role || "", platform: prefill.platform || "", url: "", status: "applied", interview_date: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.company.trim() || !form.role.trim()) { toast.error("Company and role are required"); return; }
    setBusy(true);
    try {
      await api.post("/applications", form);
      toast.success("Application tracked ✓");
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="application-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <Input placeholder="Company *" value={form.company} onChange={set("company")} data-testid="application-company-input" />
        <Input placeholder="Role *" value={form.role} onChange={set("role")} data-testid="application-role-input" />
        <Input placeholder="Platform (LinkedIn, Indeed…)" value={form.platform} onChange={set("platform")} data-testid="application-platform-input" />
        <Input placeholder="Job link (optional)" value={form.url} onChange={set("url")} data-testid="application-url-input" />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select value={form.status} onChange={set("status")} data-testid="application-status-select"
          className="h-10 rounded-md border border-border bg-background px-3 text-sm">
          {ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label.replace(" 🎉", "")}</option>)}
        </select>
        {form.status === "interview" && (
          <Input type="date" value={form.interview_date || ""} onChange={set("interview_date")}
            className="h-10 w-44" data-testid="application-interview-date-input" title="Interview date — we'll remind you the day before" />
        )}
        <Button onClick={submit} disabled={busy} data-testid="application-save-btn">{busy ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}

function AppRow({ app, onChange }) {
  const meta = STATUS_META[app.status] || STATUS_META.saved;

  const setStatus = async (status) => {
    try {
      await api.patch(`/applications/${app.application_id}`, { status });
      if (status === "offer") toast.success("OFFER! Huge win — go get it 🎉");
      else if (status === "interview") toast.success("Interview stage! Set the date below and we'll remind you the day before 📅");
      else toast.success(`Moved to ${STATUS_META[status].label.replace(" 🎉", "")}`);
      onChange();
    } catch { toast.error("Could not update"); }
  };

  const setInterviewDate = async (interview_date) => {
    try {
      await api.patch(`/applications/${app.application_id}`, { interview_date });
      if (interview_date) toast.success("Reminder set — you'll get a nudge the day before 📅");
      onChange();
    } catch { toast.error("Could not save date"); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this application?")) return;
    try { await api.delete(`/applications/${app.application_id}`); onChange(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <li className="rounded-2xl border border-border bg-card p-4" data-testid={`application-row-${app.application_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium">{app.role} <span className="text-muted-foreground">@ {app.company}</span></p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {app.platform || "Direct"} · Updated {new Date(app.updated_at).toLocaleDateString()}
            {app.url ? <a href={app.url} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center text-primary hover:underline">Job link <ExternalLink className="ml-0.5 h-3 w-3" /></a> : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2.5 py-1 text-xs ${meta.cls}`}>{meta.label}</span>
          {app.status === "interview" && (
            <input type="date" value={app.interview_date || ""} onChange={(e) => setInterviewDate(e.target.value)}
              data-testid={`application-interview-date-${app.application_id}`}
              className="h-9 rounded-md border border-border bg-background px-2 text-xs"
              title="Interview date — WhatsApp + in-app reminder the day before" />
          )}
          <select value={app.status} onChange={(e) => setStatus(e.target.value)} data-testid={`application-status-${app.application_id}`}
            className="h-9 rounded-md border border-border bg-background px-2 text-xs">
            {ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label.replace(" 🎉", "")}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={remove} data-testid={`application-delete-${app.application_id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </li>
  );
}
